/**
 * Ducts Discipline Configuration
 */

export const FIELD_MAP = {
  buildingNumber: 'building_number',
  area: 'area',
  subArea: 'sub_area',
  elevation: 'elevation',
  floor: 'floor',
  equipmentTagOrCode: 'equipment_tag_or_code',
  description: 'description',
  budgetedHr: 'budgeted_hr',
  credit: 'credit',
  costCode: 'cost_code',
  sovCode: 'sov_code',
  sovDescription: 'sov_description',
  deliveryToSite: 'delivery_to_site',
  roughSet: 'rough_set',
  supports: 'supports',
  bolting: 'bolting',
  accessories: 'accessories',
  grout: 'grout',
  drawingNumber: 'drawing_number',
  pAndId: 'p_and_id',
  divisionArea: 'division_area',
  subDivisionArea: 'sub_division_area',
  activityId: 'activity_id',
  activityName: 'activity_name',
  comments: 'comments',
  udf01: 'udf01',
  udf02: 'udf02',
  udf03: 'udf03',
  udf04: 'udf04',
  udf05: 'udf05',
  udf06: 'udf06',
  udf07: 'udf07',
  udf08: 'udf08',
  udf09: 'udf09',
  udf10: 'udf10',
};

export const FIELD_COMPLETION_COLUMNS = [
  'deliveryToSite',
  'roughSet',
  'supports',
  'bolting',
  'accessories',
  'grout',
];

export const EDITABLE_COLUMNS = [
  'activityId', 'activityName',
  'buildingNumber', 'area', 'subArea', 'elevation', 'floor',
  'equipmentTagOrCode', 'description', 'budgetedHr', 'credit', 'costCode', 'sovCode',
  'deliveryToSite', 'roughSet', 'supports', 'bolting', 'accessories', 'grout',
  'drawingNumber', 'pAndId', 'divisionArea', 'subDivisionArea',
  'comments', 'udf01', 'udf02', 'udf03', 'udf04', 'udf05',
  'udf06', 'udf07', 'udf08', 'udf09', 'udf10',
];

export const transformMatrixData = (matrixData) => {
  return matrixData.map((row, index) => ({
    id: row.id || index,
    area: row.area || '',
    subArea: row.sub_area || '',
    buildingNumber: row.building_number || '',
    floor: row.floor || '',
    divisionArea: row.division_area || '',
    subDivisionArea: row.sub_division_area || '',
    jobNumber: row.job_number || '',
    activityId: row.activity_id || '',
    activityName: row.activity_name || '',
    costCode: row.cost_code || '',
    sovCode: row.sov_code || '',
    sovDescription: row.sov_description || '',
    elevation: row.elevation || '',
    pAndId: row.p_and_id || '',
    drawingNumber: row.drawing_number || '',
    equipmentTagOrCode: row.equipment_tag_or_code || '',
    description: row.description || '',
    deliveryToSite: row.delivery_to_site ? Math.round(row.delivery_to_site) : 0,
    roughSet: row.rough_set ? Math.round(row.rough_set) : 0,
    supports: row.supports ? Math.round(row.supports) : 0,
    bolting: row.bolting ? Math.round(row.bolting) : 0,
    accessories: row.accessories ? Math.round(row.accessories) : 0,
    grout: row.grout ? Math.round(row.grout) : 0,
    totalFieldCompletion: row.total_field_completion ? Math.round(row.total_field_completion) : 0,
    budgetedHr: row.budgeted_hr ? Number(row.budgeted_hr) : 0,
    credit: row.credit ? Number(row.credit) : 0,
    earnedHr: row.earned_hr ? Number(row.earned_hr) : 0,
    earnedCredit: row.earned_credit ? Number(row.earned_credit) : 0,
    createdAt: row.created_at || '',
    updatedAt: row.updated_at || '',
    comments: row.comments || '',
    udf01: row.udf01 || '',
    udf02: row.udf02 || '',
    udf03: row.udf03 || '',
    udf04: row.udf04 || '',
    udf05: row.udf05 || '',
    udf06: row.udf06 || '',
    udf07: row.udf07 || '',
    udf08: row.udf08 || '',
    udf09: row.udf09 || '',
    udf10: row.udf10 || '',
  }));
};

export const buildChangePayload = (rowId, columnId, oldValue, newValue) => {
  const backendField = FIELD_MAP[columnId];
  if (!backendField) return null;
  return { id: rowId, changes: { [backendField]: newValue } };
};

export const ductsConfig = {
  id: 'ducts',
  name: 'Ducts',
  menuId: 'mechanical-ducts',

  // Pin the selection checkbox + Equipment Tag + Description columns on horizontal scroll
  frozenColumnIds: ['equipmentTagOrCode', 'description'],
  freezeActionColumns: true,
  tableName: 'ducts_tracking',
  historyTableName: 'ducts_tracking_history',
  
  api: {
    getData: '/discipline-matrix/ducts/project/{projectId}',
    saveChanges: '/discipline-matrix/ducts/save-changes',
    export: '/discipline-matrix/ducts/export/{projectId}',
    upload: '/discipline-matrix/ducts/upload',
    priorities: '/priorities',
    columnMappings: '/discipline-matrix/ducts/project/{projectId}/column-mappings',
  },
  
  // Permission requirements (pt_* = Progress Tracking module)
  permissions: {
    edit: 'pt_edit',
    analytics: 'pt_analytics',
    import: 'pt_import',
    export: 'pt_export',
    template: 'pt_template',
    columnConfig: 'pt_columnconfig',
    roc: 'pt_roc',
    sfRatio: 'pt_sfratio',
    matBore: 'pt_matbore',
    delete: 'pt_delete',
  },
  
  columns: [
    // Dashboard columns - full names
    { id: 'id', header: 'ID', accessor: 'id', dbField: 'id', width: 50, editable: false, filterable: true, type: 'number', showInDashboard: true },
    { id: 'area', header: 'Area', accessor: 'area', dbField: 'area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'subArea', header: 'Sub Area', accessor: 'subArea', dbField: 'sub_area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'buildingNumber', header: 'Building Number', accessor: 'buildingNumber', dbField: 'building_number', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'floor', header: 'Floor', accessor: 'floor', dbField: 'floor', width: 60, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'divisionArea', header: 'Division Area', accessor: 'divisionArea', dbField: 'division_area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'subDivisionArea', header: 'Sub Division Area', accessor: 'subDivisionArea', dbField: 'sub_division_area', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'jobNumber', header: 'Job Number', accessor: 'jobNumber', dbField: 'job_number', width: 80, editable: false, filterable: true, type: 'text', showInDashboard: true },
    { id: 'activityId', header: 'Activity ID', accessor: 'activityId', dbField: 'activity_id', width: 130, editable: true, filterable: true, type: 'text', showInDashboard: true, headerGroup: 'Schedule' },
    { id: 'activityName', header: 'Activity Name', accessor: 'activityName', dbField: 'activity_name', width: 160, editable: true, filterable: true, type: 'text', showInDashboard: false, headerGroup: 'Schedule' },
    { id: 'costCode', header: 'Cost Code', accessor: 'costCode', dbField: 'cost_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'sovCode', header: 'SOV Code', accessor: 'sovCode', dbField: 'sov_code', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'elevation', header: 'Elevation', accessor: 'elevation', dbField: 'elevation', width: 70, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'pAndId', header: 'P&ID', accessor: 'pAndId', dbField: 'p_and_id', width: 70, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'drawingNumber', header: 'Drawing Number', accessor: 'drawingNumber', dbField: 'drawing_number', width: 80, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'equipmentTagOrCode', header: 'Equipment Tag/Code', accessor: 'equipmentTagOrCode', dbField: 'equipment_tag_or_code', width: 90, editable: true, filterable: true, type: 'text', showInDashboard: true },
    { id: 'description', header: 'Description', accessor: 'description', dbField: 'description', width: 120, editable: true, filterable: true, type: 'text', showInDashboard: true },
    // Progress columns
    { id: 'deliveryToSite', header: 'Delivery to Site %', accessor: 'deliveryToSite', dbField: 'delivery_to_site', width: 75, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#FCE4EC', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'roughSet', header: 'Rough Set %', accessor: 'roughSet', dbField: 'rough_set', width: 70, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#FCE4EC', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'supports', header: 'Supports %', accessor: 'supports', dbField: 'supports', width: 70, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#FCE4EC', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'bolting', header: 'Bolting %', accessor: 'bolting', dbField: 'bolting', width: 65, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#FCE4EC', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'accessories', header: 'Accessories %', accessor: 'accessories', dbField: 'accessories', width: 75, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#FCE4EC', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'grout', header: 'Grout %', accessor: 'grout', dbField: 'grout', width: 65, editable: true, filterable: true, type: 'progress', options: [0, 25, 50, 75, 100], affectsCalculation: 'totalFieldCompletion', headerColor: '#FCE4EC', headerGroup: 'Field Progress', showInDashboard: true },
    { id: 'totalFieldCompletion', header: 'Total Field Completion %', accessor: 'totalFieldCompletion', dbField: 'total_field_completion', width: 75, editable: false, filterable: true, type: 'progress-readonly', computed: true, headerColor: '#FCE4EC', headerGroup: 'Field Progress', showInDashboard: true },
    // Hidden from dashboard
    { id: 'budgetedHr', header: 'Budgeted Hr', accessor: 'budgetedHr', dbField: 'budgeted_hr', width: 90, editable: true, filterable: true, type: 'number', showInDashboard: false },
    { id: 'credit', header: 'Credit', accessor: 'credit', dbField: 'credit', width: 70, editable: true, filterable: true, type: 'number', showInDashboard: false },
    { id: 'earnedHr', header: 'Earned Hr', accessor: 'earnedHr', dbField: 'earned_hr', width: 90, editable: false, filterable: true, type: 'number', computed: true, showInDashboard: false },
    { id: 'earnedCredit', header: 'Earned Credit', accessor: 'earnedCredit', dbField: 'earned_credit', width: 100, editable: false, filterable: true, type: 'number', computed: true, showInDashboard: false },
    { id: 'createdAt', header: 'Created At', accessor: 'createdAt', dbField: 'created_at', width: 130, editable: false, filterable: true, type: 'datetime', showInDashboard: false },
    { id: 'updatedAt', header: 'Updated At', accessor: 'updatedAt', dbField: 'updated_at', width: 130, editable: false, filterable: true, type: 'datetime', showInDashboard: false },
    { id: 'comments', header: 'Comments', accessor: 'comments', dbField: 'comments', width: 120, editable: true, filterable: true, type: 'text', showInDashboard: false },
  ],
  
  udfColumns: Array.from({ length: 10 }, (_, i) => ({
    id: `udf${String(i + 1).padStart(2, '0')}`,
    header: `UDF ${String(i + 1).padStart(2, '0')}`,
    accessor: `udf${String(i + 1).padStart(2, '0')}`,
    dbField: `udf${String(i + 1).padStart(2, '0')}`,
    width: 100, editable: true, filterable: true, type: 'text', isUdf: true,
  })),
  
  fieldMap: FIELD_MAP,
  editableColumns: EDITABLE_COLUMNS,
  transformData: transformMatrixData,
  buildChangePayload: buildChangePayload,
  
  features: {
    dragFill: true,
    progressTrends: true,
    matBore: false,
    rulesOfCredit: true,
    columnConfig: true,
    actualSpentUpload: false,
    export: true,
    upload: true,
    presence: true,
  },
};

export default ductsConfig;

